import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HeightOutlinedIcon from "@mui/icons-material/HeightOutlined";
import MonitorWeightOutlinedIcon from "@mui/icons-material/MonitorWeightOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Modal,
  Paper,
  InputAdornment,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { grantClientPro } from "../../../redux/thunks/thunk-partners";
import { fetchClientData } from "../../../redux/thunks/thunk-clients";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { User } from "../../../models/types"; // Import the full User type
import ExerciseBankVault from "./routes/vault";
import Client from "./client";
import Dashboard from "./routes/dashboard";
import GlassContainer from "../components/liquid-glass";
import { usePrefersDarkMode } from "../../home/homev3";
import { toggleDarkMode } from "../../../redux/slices/partnersSlice";
import PartnerSidebar from "./PartnerSidebar";
import InactiveBackground from "./components/inactive-background";
import ClientQuizModal from "./components/client-quiz-modal";


// Define the ClientData interface (used for listing clients)
export interface ClientData {
  clientUserId: string;
  name: string;
  gender: "male" | "female";
  birthday: Date | string;
  height: number; // cm
  weight: number; // kg
  activityLevel:
  | "sedentary"
  | "lightly active"
  | "moderately active"
  | "very active"
  | "extra active";
}

// Update your Cognito User Pool details.
const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

// Styling for the modal dialog
const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

type ContentKey = "overview" | "clients" | "vault";



type SortKey = "name" | "gender" | "age" | "height" | "weight" | "bmi";
type VaultSection = "exercises" | "templates" | "recipes" | "mealPlans";

export default function PartnersLoggedIn() {
  const dispatch = useDispatch<AppDispatch>();

  const isDark = usePrefersDarkMode();
  const darkmode = useSelector((state: RootState) => state.partners.darkMode);

  // run only once on initial mount, never again
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return; // we've already synced once

    // mark that we've attempted sync
    didInitRef.current = true;

    // if redux and system disagree on first mount, flip redux once
    if (darkmode !== isDark) {
      dispatch(toggleDarkMode());
    }
  }, [darkmode, isDark, dispatch]);



  // Main content selection
  const [activeContent, setActiveContent] = useState<ContentKey>("overview");
  const [activeTitle, setActiveTitle] = useState<string>("Overview");
  const [activeVaultSection, setActiveVaultSection] =
    useState<VaultSection | null>(null);

  // Sidebar collapse state (manual only)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Instead of using a dummy token, we now set up a state to hold the refreshed token.
  const [idToken, setIdToken] = useState<string>("");

  // Local state for partner clients and UI handling.
  const partnerDataState = useSelector(
    (state: RootState) => state.partners?.partnerData
  );

  // Logged-in partner full object (for vault counts etc.)
  const partner: User | null = useSelector(
    (state: RootState) =>
      state.partners?.partnerData?.partnerData as User | null
  );

  // Trainer (partner) userId from store — needed to revoke pro (remove)
  const trainerUserId: string | undefined = useSelector(
    (state: RootState) => state.partners?.partnerData?.partnerData?.userId
  );

  // Optional loading flag from the partners slice, if your slice exposes it
  const partnerLoadingFromStore =
    useSelector(
      (state: RootState) =>
        (state.partners as any)?.loading ||
        (state.partners as any)?.status === "loading"
    ) ?? false;

  const initialClients: ClientData[] =
    partnerDataState?.partnerData?.partnerClients ?? [];
  const [partnerClients, setPartnerClients] =
    useState<ClientData[]>(initialClients);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newClientUserId, setNewClientUserId] = useState<string>("");
  const [isQuizOpen, setIsQuizOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Full selected client object for the right pane
  const [selectedClient, setSelectedClient] = useState<User | null>(null);

  // Track immediate visual selection (before fetch resolves)
  const [pendingSelectedId, setPendingSelectedId] = useState<string | null>(
    null
  );

  // Local loading flags
  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(true);
  const [isFetchingPartner, setIsFetchingPartner] = useState<boolean>(false);
  const [isFetchingClient, setIsFetchingClient] = useState<boolean>(false);

  const darkMode = useSelector((state: RootState) => state.partners.darkMode);

  const [sortBy, setSortBy] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");



  // On mount, retrieve the current Cognito session and refresh the token.
  useEffect(() => {
    setIsFetchingToken(true);
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.error("Error retrieving session:", err);
          setIsFetchingToken(false);
          return;
        }
        if (session && session.isValid()) {
          setIdToken(session.getIdToken().getJwtToken());
        }
        setIsFetchingToken(false);
      });
    } else {
      setIsFetchingToken(false);
    }
  }, []);

  // Update local partnerClients if the redux store changes.
  useEffect(() => {
    setPartnerClients(partnerDataState?.partnerData?.partnerClients ?? []);
  }, [partnerDataState]);

  const calculateAge = (birthday: Date | string | null | undefined) => {
    if (!birthday) return null;
    const dob = new Date(birthday);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const cmToFeetInches = (cm?: number | null) => {
    if (!cm || Number.isNaN(cm)) return "—";
    const totalInches = Math.round(cm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}' ${inches}"`;
  };

  const kgToLbs = (kg?: number | null) => {
    if (!kg || Number.isNaN(kg)) return "—";
    const lbs = Math.round(kg * 2.20462);
    return `${lbs} lbs`;
  };

  const calculateBMI = (heightCm?: number | null, weightKg?: number | null) => {
    if (!heightCm || !weightKg) return null;
    const m = heightCm / 100;
    if (!m) return null;
    const bmi = weightKg / (m * m);
    if (!Number.isFinite(bmi)) return null;
    return Math.round(bmi);
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  // Filter + sort clients for the table view
  const filteredClients = useMemo(() => {
    let list = partnerClients ?? [];

    // Global search by name
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(term));
    }

    // Sorting
    const sorted = [...list].sort((a, b) => {
      let aVal: string | number | null = null;
      let bVal: string | number | null = null;

      switch (sortBy) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "gender":
          aVal = (a.gender || "").toString().toLowerCase();
          bVal = (b.gender || "").toString().toLowerCase();
          break;
        case "age":
          aVal = calculateAge(a.birthday) ?? 0;
          bVal = calculateAge(b.birthday) ?? 0;
          break;
        case "height":
          aVal = a.height ?? 0;
          bVal = b.height ?? 0;
          break;
        case "weight":
          aVal = a.weight ?? 0;
          bVal = b.weight ?? 0;
          break;
        case "bmi":
          aVal = calculateBMI(a.height, a.weight) ?? 0;
          bVal = calculateBMI(b.height, b.weight) ?? 0;
          break;
        default:
          break;
      }

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return sortDirection === "asc" ? 1 : -1;
      if (bVal === null) return sortDirection === "asc" ? -1 : 1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = aVal.toString();
      const bStr = bVal.toString();
      const cmp = aStr.localeCompare(bStr);
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [partnerClients, searchTerm, sortBy, sortDirection]);

  // Clear selection if the currently selected client no longer exists in the list
  useEffect(() => {
    if (!selectedClient) return;
    const stillExists = partnerClients.some(
      (c) => c.clientUserId === selectedClient.userId
    );
    if (!stillExists) {
      setSelectedClient(null);
      setPendingSelectedId(null);
    }
  }, [partnerClients, selectedClient?.userId]);

  const handleAddClient = async () => {
    if (!newClientUserId) {
      setError("Please enter a client userId.");
      return;
    }
    if (!idToken) {
      setError("Missing auth token. Please sign in again.");
      return;
    }
    if (!trainerUserId) {
      setError("Missing trainer ID. Try reloading the page.");
      return;
    }

    setIsFetchingPartner(true);
    setError("");

    try {
      // 1) Grant PRO + link trainer on the server (also upserts partnerClients)
      const { client, partner } = await dispatch(
        grantClientPro({
          client: newClientUserId, // can be User or userId; we pass the id
          trainerUserId, // link to this trainer
        })
      ).unwrap();

      // 2) Refresh local list from the updated partner payload in Redux
      const updatedList = (partner as any)?.partnerClients ?? [];
      setPartnerClients(updatedList);

      // 3) Optionally fetch and select the new client in the right pane
      await dispatch(
        fetchClientData({ idToken, clientUserId: (client as any).userId })
      ).unwrap();
      setSelectedClient({
        ...client,
        birthday: new Date((client as any).birthday),
      } as User);

      // 4) Close modal / reset
      setIsModalOpen(false);
      setNewClientUserId("");
    } catch (e: any) {
      setError(e?.message || "Failed to add client and grant access.");
    } finally {
      setIsFetchingPartner(false);
    }
  };

  // On client click: fetch details, then switch to detail view
  const handleClientClick = (clientUserId: string) => {
    if (!idToken) return;

    // immediately mark as pending for visual feedback
    setPendingSelectedId(clientUserId);

    setIsFetchingClient(true);
    dispatch(fetchClientData({ idToken, clientUserId }))
      .unwrap()
      .then((data: User) => {
        const clientData = { ...data, birthday: new Date(data.birthday) };
        setSelectedClient(clientData);
        setPendingSelectedId(null);

        // Reset sorting/search inputs when going into a client
        setSortBy("name");
        setSortDirection("asc");
        setSearchTerm("");
      })
      .catch((err: any) => {
        console.error("Error fetching client data:", err);
        setPendingSelectedId(null);
      })
      .finally(() => setIsFetchingClient(false));
  };

  // Allow children (Dashboard) to open a client here + switch view.
  const openClientFromAnywhere = (clientUserId: string) => {
    setActiveContent("clients");
    setActiveTitle("Clients");
    // Ensure the sidebar is visible so the selection is clear
    setIsSidebarCollapsed(false);
    // Trigger the same fetch/selection routine used by clicks
    handleClientClick(clientUserId);
  };

  // Unified flags
  const partnerLoading =
    partnerLoadingFromStore ||
    isFetchingPartner ||
    (!partnerDataState?.partnerData && !isFetchingToken);

  const clientLoading = isFetchingClient;

  // Branded loading overlay — visible against dark/light backgrounds
  const CenteredSpinner = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        borderRadius: 3,
        backdropFilter: "blur(12px)",
        background: darkMode
          ? "rgba(0,0,0,0.35)"
          : "rgba(255,255,255,0.5)",
        animation: "fadeIn 0.3s ease",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        component="img"
        src="/icon192.png"
        alt="Loading"
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          animation: "pulse 1.6s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0.6, transform: "scale(0.92)" },
          },
        }}
      />
      <CircularProgress
        size={28}
        thickness={4}
        sx={{ color: "primary.main" }}
      />
      <Typography
        variant="body2"
        sx={{
          color: darkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
          fontWeight: 500,
          letterSpacing: 0.3,
        }}
      >
        Loading client…
      </Typography>
    </Box>
  );

  // selection logic for list items (used for row highlighting)
  const isItemSelected = (clientId: string) =>
    pendingSelectedId === clientId || selectedClient?.userId === clientId;



  const handleNavClick = (
    label: string,
    content: ContentKey | null,
    comingSoon?: boolean,
    vaultSection?: VaultSection | null
  ) => {
    if (!content || comingSoon) return;
    setActiveContent(content);
    setActiveTitle(label);

    if (content === "vault") {
      setActiveVaultSection(vaultSection ?? null);
    } else {
      setActiveVaultSection(null);
    }

    // When switching away, reset detail view
    if (content !== "clients") {
      setSelectedClient(null);
      setPendingSelectedId(null);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <InactiveBackground />

      {/* LEFT SIDEBAR */}
      <PartnerSidebar
        activeContent={activeContent}
        activeTitle={activeTitle}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        onNavClick={handleNavClick}
        onAddClient={() => setIsModalOpen(true)}
        onOpenQuiz={() => setIsQuizOpen(true)}
        partnerLoading={partnerLoading}
        hasToken={!!idToken}
        activeVaultSection={activeVaultSection}
      />
      <ClientQuizModal
        open={isQuizOpen}
        onClose={() => setIsQuizOpen(false)}
        clients={partnerClients}
      />

      {/* RIGHT: CONTENT AREA */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100vh",
        }}
      >
        {/* Main content area */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            p: 2,
            overflow: activeContent === "overview" ? "hidden" : "auto",
          }}
        >
          {/* Token gating overlay (only relevant for Clients view) */}
          {activeContent === "clients" && isFetchingToken && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "background.default",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.9,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Clients: full-width table list OR detail view */}
          {activeContent === "clients" && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {selectedClient ? (
                <>
                  {/* Back arrow circle */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1.5,
                      gap: 1,
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        setSelectedClient(null);
                        setPendingSelectedId(null);
                      }}
                      sx={{
                        borderRadius: "50%",
                        border: "1px solid",
                        borderColor: darkMode ? "grey.700" : "grey.300",
                        backgroundColor: darkMode
                          ? "rgba(255,255,255,0.04)"
                          : "white",
                        boxShadow: 1,
                      }}
                    >
                      <ArrowBackIosNewIcon
                        fontSize="small"
                        sx={{ color: darkMode ? "white" : "black" }}
                      />
                    </IconButton>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: darkMode ? "white" : "black" }}
                    >
                      Back to client list
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    {clientLoading ? (
                      CenteredSpinner
                    ) : (
                      <Client
                        client={selectedClient}
                        onRemoved={() => {
                          setSelectedClient(null);
                          setPendingSelectedId(null);
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : partnerLoading ? (
                CenteredSpinner
              ) : (
                <>
                  {/* Top search bar */}
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <TextField
                      variant="outlined"
                      placeholder="Search by name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        maxWidth: 320,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 4,
                          height: 40,
                          backgroundColor: darkMode
                            ? "rgba(255,255,255,0.08)"
                            : "inherit",
                          "& fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.4)"
                              : undefined,
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode
                              ? "rgba(255,255,255,0.7)"
                              : undefined,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: darkMode ? "white" : "inherit",
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: darkMode ? "rgba(255,255,255,0.7)" : undefined,
                          opacity: 1,
                        },
                        "& .MuiSvgIcon-root": {
                          color: darkMode ? "white" : "inherit",
                        },
                      }}
                    />
                  </Box>

                  {filteredClients.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: darkMode ? "white" : "text.primary" }}
                    >
                      No clients available.
                    </Typography>
                  ) : (
                    <GlassContainer
                      sx={{
                        flex: 1,
                        borderRadius: 3,
                        overflow: "hidden",
                        display: "flex",
                      }}
                    >
                      <TableContainer
                        sx={{
                          flex: 1,
                          maxHeight: "100%",
                          backgroundColor: "transparent",
                        }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                "& th": {
                                  borderBottom: "1px solid",
                                  borderBottomColor: darkMode
                                    ? "rgba(255,255,255,0.06)"
                                    : "rgba(0,0,0,0.06)",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <PersonOutlineOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "name"}
                                    direction={
                                      sortBy === "name" ? sortDirection : "asc"
                                    }
                                    onClick={() => handleSort("name")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    Name
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <WcOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "gender"}
                                    direction={
                                      sortBy === "gender"
                                        ? sortDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("gender")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    Gender
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <CalendarMonthOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "age"}
                                    direction={
                                      sortBy === "age" ? sortDirection : "asc"
                                    }
                                    onClick={() => handleSort("age")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    Age
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <HeightOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "height"}
                                    direction={
                                      sortBy === "height"
                                        ? sortDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("height")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    Height
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <MonitorWeightOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "weight"}
                                    direction={
                                      sortBy === "weight"
                                        ? sortDirection
                                        : "asc"
                                    }
                                    onClick={() => handleSort("weight")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    Weight
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  backgroundColor: darkMode
                                    ? "rgba(255,255,255,0.04)"
                                    : "grey.50",
                                  color: darkMode ? "white" : "black",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <MonitorHeartOutlinedIcon fontSize="small" />
                                  <TableSortLabel
                                    active={sortBy === "bmi"}
                                    direction={
                                      sortBy === "bmi" ? sortDirection : "asc"
                                    }
                                    onClick={() => handleSort("bmi")}
                                    sx={{
                                      "&.Mui-active": {
                                        color: darkMode
                                          ? "white !important"
                                          : "black",
                                      },
                                      "& .MuiTableSortLabel-icon": {
                                        color: darkMode
                                          ? "white !important"
                                          : "inherit",
                                      },
                                    }}
                                  >
                                    BMI
                                  </TableSortLabel>
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredClients.map((client) => {
                              const ageVal = calculateAge(client.birthday);
                              const selected = isItemSelected(
                                client.clientUserId
                              );
                              const bmiVal = calculateBMI(
                                client.height,
                                client.weight
                              );

                              const isMale = client.gender === "male";

                              const genderChip = (
                                <Chip
                                  label={
                                    client.gender
                                      ? client.gender.charAt(0).toUpperCase() +
                                      client.gender.slice(1)
                                      : "—"
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: isMale
                                      ? "#bbdefb"
                                      : "#f8bbd0",
                                    borderColor: isMale ? "#1976d2" : "#c2185b",
                                    borderWidth: 1,
                                    borderStyle: "solid",
                                    color: isMale ? "#0d47a1" : "#880e4f",
                                    fontWeight: 600,
                                  }}
                                />
                              );

                              const isPending =
                                pendingSelectedId === client.clientUserId &&
                                clientLoading;

                              return (
                                <TableRow
                                  key={client.clientUserId}
                                  hover
                                  onClick={() =>
                                    handleClientClick(client.clientUserId)
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    backgroundColor: selected
                                      ? darkMode
                                        ? "rgba(255,255,255,0.06)"
                                        : "rgba(0,0,0,0.04)"
                                      : "inherit",
                                    "& .MuiTableCell-root": {
                                      py: 1.5,
                                      borderBottom: "1px solid",
                                      borderBottomColor: darkMode
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      color: darkMode
                                        ? "white"
                                        : "text.primary",
                                      fontWeight: selected ? 600 : 400,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <span>{client.name}</span>
                                      {isPending && (
                                        <CircularProgress size={16} />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{genderChip}</TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkMode
                                        ? "white"
                                        : "text.primary",
                                    }}
                                  >
                                    {ageVal !== null ? ageVal : "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkMode
                                        ? "white"
                                        : "text.primary",
                                    }}
                                  >
                                    {cmToFeetInches(client.height)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkMode
                                        ? "white"
                                        : "text.primary",
                                    }}
                                  >
                                    {kgToLbs(client.weight)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkMode
                                        ? "white"
                                        : "text.primary",
                                    }}
                                  >
                                    {bmiVal !== null ? bmiVal : "—"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </GlassContainer>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Overview / Dashboard */}
          {activeContent === "overview" && (
            <Dashboard onNavigateToClient={openClientFromAnywhere} />
          )}

          {/* Exercise Bank Vault (used for all vault views) */}
          {activeContent === "vault" && (
            <Box sx={{ minHeight: 360 }}>
              <ExerciseBankVault
                key={activeVaultSection ?? "all"}
                initialSection={activeVaultSection ?? null}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Modal for adding a new client */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Paper sx={modalStyle}>
          <Typography variant="h6" gutterBottom>
            Add Client
          </Typography>
          <TextField
            label="User ID"
            variant="outlined"
            fullWidth
            value={newClientUserId}
            onChange={(e) => setNewClientUserId(e.target.value)}
          />
          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={() => setIsModalOpen(false)}
              color="secondary"
              sx={{ mr: 1 }}
              disabled={isFetchingPartner}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddClient}
              disabled={!idToken || isFetchingPartner}
            >
              {isFetchingPartner ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  Saving…
                </Box>
              ) : (
                "Add"
              )}
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
}
